import React, { useState, useMemo, useEffect, useRef, Suspense } from "react";
import { Sentry } from "./observability";
import { SeoMeta } from "./components/SeoMeta";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Toaster } from "sonner";
import "./App.css";
import { BrowserRouter, Routes, Route, Outlet, useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { translations } from "./i18n/translations";
import { buildDetectedIssues } from "./utils/scoring";
import { getVerdictKey, getPaywallTeaserKey, getVerdictBg, getVerdictShortExplanationKey, formatDate, uiDateLocale } from "./utils/verdictKeys";
import { getPaidSession, cleanupStalePaidSessions } from "./utils/paidSessions";
import { trackEvent } from "./observability";

import { useDocumentMeta } from "./hooks/useDocumentMeta";
import { useAnalysisState } from "./hooks/useAnalysisState";
import { useBackSwipeGuard } from "./hooks/useBackSwipeGuard";
// (H40l) useCarsCheckedCounter hook removed — counter no longer rendered.
import { useAnalyzeFlow } from "./hooks/useAnalyzeFlow";
import { usePaymentFlow } from "./hooks/usePaymentFlow";

import { ExpertConfirmationOverlay } from "./components/ExpertConfirmationOverlay";
import { AnalyzingOverlay } from "./components/AnalyzingOverlay";
import { LandingForm } from "./components/LandingForm";
import { PreCheckoutConsentModal } from "./components/PreCheckoutConsentModal";
import { SellerCallSuccessToast } from "./components/SellerCallSuccessToast";
import { PaymentCancelledBanner } from "./components/PaymentCancelledBanner";
import { PaymentVerifyingOverlay } from "./components/PaymentVerifyingOverlay";
import { LandingBackdrop } from "./components/LandingBackdrop";
import { AppHeader, LANGUAGES } from "./components/AppHeader";
import { AppDownloadBanner } from "./components/AppDownloadBanner";
import { LegalFooter } from "./components/LegalFooter";
import { TermsPage } from "./components/legal/TermsPage";
import { PrivacyPage } from "./components/legal/PrivacyPage";
import { CookiePolicyPage } from "./components/legal/CookiePolicyPage";
import { AccessibilityPage } from "./components/legal/AccessibilityPage";
import { CookieBanner } from "./components/legal/CookieBanner";

const MobileDashboard = React.lazy(() => import("./components/dashboard/MobileDashboard").then(m => ({ default: m.MobileDashboard })));
const VerdictScreen = React.lazy(() => import("./components/VerdictScreen").then(m => ({ default: m.VerdictScreen })));
const MarketingModalHost = React.lazy(() => import("./components/MarketingModalHost").then(m => ({ default: m.MarketingModalHost })));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ErrorFallback({error, resetError}) {
  return (
    <div role="alert" className="p-4 m-4 bg-slate-900 border border-red-500 rounded-lg text-red-400">
      <p className="font-bold text-red-500 mb-2">Something went wrong:</p>
      <pre className="text-xs overflow-auto whitespace-pre-wrap">{error.message}</pre>
      <button onClick={resetError} className="mt-4 px-4 py-2 bg-red-600/20 hover:bg-red-600/40 border border-red-500/50 text-red-400 rounded transition-colors">
        Try again
      </button>
    </div>
  );
}

const Home = () => {
  // Deep-link support: /report/:reg pre-fills the registration so refresh /
  // direct links restore the report screen.
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const deepLinkReg = (params?.reg || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  const [registration, setRegistration] = useState(deepLinkReg);
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [language, setLanguage] = useState(() => {
    try { return localStorage.getItem('worthify_lang') || 'en'; } catch { return 'en'; }
  });
  // Persist language so legal pages and post-payment returns can read the user's choice.
  useEffect(() => {
    try { localStorage.setItem('worthify_lang', language); } catch { /* ignore */ }
  }, [language]);
  // Analysis state must be initialised BEFORE any hook that reads `analysis`
  // (useDocumentMeta, deep-link effect) — otherwise we hit a TDZ crash and
  // the whole <Home> component fails to mount.
  const _analysisState = useAnalysisState();
  const {
    analysis,
    showVerdict,
    pdfUnlocked,
    paymentLoading,
    paymentError,
    pdfPaymentLoading,
    setAnalysis,
    setShowVerdict,
    setPdfUnlocked,
    setPaymentLoading,
    setPaymentError,
    setPdfPaymentLoading,
  } = _analysisState;
  // Update <title> + OpenGraph + Twitter Card meta tags whenever the loaded
  // analysis changes — drives social-share previews (WhatsApp / Slack / X).
  useDocumentMeta({ analysis, registration, language });

  const [targetPrice, setTargetPrice] = useState("");
  const [vinNumber, setVinNumber] = useState("");
  // Pre-checkout consent modal — surfaces the UK Consumer Contracts Regs 2013
  // warning + checkbox before the user is redirected to Stripe.
  const [consentModal, setConsentModal] = useState({ open: false, amount: 1.0, purpose: "verdict" });
  const [paymentCancelledBanner, setPaymentCancelledBanner] = useState(false);
  const [vinError, setVinError] = useState("");
  const [vinPreview, setVinPreview] = useState(null);
  const [vinPreviewLoading, setVinPreviewLoading] = useState(false);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingProviders, setLoadingProviders] = useState({ openai: 'pending', anthropic: 'pending', gemini: 'pending' });
  const [loadingContext, setLoadingContext] = useState(null);
  const [expertConfirm, setExpertConfirm] = useState({ visible: false, from: null, to: null });
  // Green "Payment confirmed!" flash inside the payment overlay (~1.8s)
  const [paymentConfirmedFlash, setPaymentConfirmedFlash] = useState(false);
  const flashPaymentConfirmed = () => {
    setPaymentConfirmedFlash(true);
    setTimeout(() => setPaymentConfirmedFlash(false), 1800);
  };

  // Translation function
  const t = useMemo(() => {
    const lang = translations[language] || translations.en;
    return (key) => lang[key] || translations.en[key] || key;
  }, [language]);

  // Landing page social-proof counter removed (H40l).

  // ── Analysis pipeline (job start/poll, add-VIN, new-search+pay) ──
  const { analyzeVehicle, reanalyzeWithVin, startNewSearchWithPayment } = useAnalyzeFlow({
    API, t, language, registration, vinNumber, targetPrice, analysis,
    navigate, location,
    setError, setLoading, setAnalysis, setShowVerdict, setPdfUnlocked,
    setRegistration, setVinNumber, setTargetPrice,
    setShowLoadingOverlay, setLoadingProgress, setLoadingProviders, setLoadingContext,
  });

  // ── Stripe payment flows (£1 verdict + £4.99 PDF) ──
  const {
    downloadingPdf,
    pollPaymentStatus,
    pollPdfPaymentStatus,
    initiatePayment,
    performStripeRedirect,
    initiatePdfPayment,
  } = usePaymentFlow({
    API, t, language, registration, targetPrice, analysis,
    analysisState: _analysisState,
    consentModal, setConsentModal,
    setExpertConfirm, setAnalysis, setShowVerdict, setPdfUnlocked,
    setPaymentLoading, setPdfPaymentLoading, setPaymentError, setError,
    pdfUnlocked, pdfPaymentLoading,
    onPaymentConfirmed: flashPaymentConfirmed,
  });

  // Deep-link auto-load: when user lands on /report/:reg directly (or refreshes
  // the page after analysis, or returns from a Stripe £2.99 redirect), kick off
  // the analysis flow exactly once.
  //
  // NOTE: we deliberately do NOT use setTimeout + clearTimeout cleanup here.
  // React 18 StrictMode runs the effect twice in development; the cleanup of
  // the first run would clearTimeout the scheduled analyze, while the second
  // run hits the ref guard and bails out → analyze never fires and the user
  // sits on the hero forever (the exact symptom of the £2.99 redirect bug).
  const deepLinkLoadedRef = useRef(false);
  useEffect(() => {
    if (deepLinkLoadedRef.current) return;
    if (!deepLinkReg || analysis) return;
    deepLinkLoadedRef.current = true;
    analyzeVehicle({ registration: deepLinkReg });
  }, [deepLinkReg]); // eslint-disable-line

  const detectedIssues = useMemo(() => buildDetectedIssues(analysis, t), [analysis, t]);
  const useResultHeaderStyle = Boolean(analysis && !showVerdict);

  useEffect(() => {
    const activeRegistration = analysis?.vehicle?.registration;
    if (!activeRegistration) {
      setPdfUnlocked(false);
      return;
    }

    // ── Premium unlock derivation (Feb 2026 paywall-leak fix) ──
    // We CANNOT trust a bare `worthifyPdfUnlockedReg = "<reg>"` flag — every
    // re-search of the same registration would have flipped pdfUnlocked=true
    // even in a fresh browser/incognito session. Instead, premium is unlocked
    // ONLY when the SERVER returned a non-trimmed payload (i.e. `ai_analysis`
    // present and no `_paywall_locked` marker). The backend gates the payload
    // strictly by `session_id`, so this client-side derivation cannot leak
    // premium content to a non-paying visitor.
    // Premium (£4.99) is unlocked ONLY when the backend returned a non-trimmed
    // payload AND it isn't flagged as `_premium_locked` (which means only the
    // £1 tier was paid). The trim leaves `ai_analysis` present-but-empty in
    // that case so the frontend can still render the locked dashboard tiles.
    const aiPresent = !!analysis?.ai_analysis;
    const premiumLockedFlag = !!analysis?._paywall_locked || !!analysis?._premium_locked || !!analysis?.ai_analysis?._premium_locked;
    const serverGrantedFull = aiPresent && !premiumLockedFlag;
    setPdfUnlocked(serverGrantedFull);

    // Legacy cleanup: nuke the old localStorage flag so any cached "unlocked"
    // state from before this fix can't leak across sessions.
    try {
      if (localStorage.getItem('worthifyPdfUnlockedReg')) {
        localStorage.removeItem('worthifyPdfUnlockedReg');
      }
    } catch { /* ignore */ }
  }, [analysis?.vehicle?.registration, analysis?.ai_analysis, analysis?._paywall_locked]);

  // Fire `report_viewed` when the dashboard first becomes visible (after
  // payment OR direct cached load). Useful for funnel drop-off analytics.
  useEffect(() => {
    if (analysis && !showVerdict) {
      trackEvent('report_viewed', {
        registration: analysis?.vehicle?.registration,
        pdf_unlocked: pdfUnlocked,
        risk_level: analysis?.risk_level,
      });
    }
  }, [analysis?.vehicle?.registration, showVerdict]);

  // ── Back-swipe guard (see hooks/useBackSwipeGuard.js) ─────────────
  useBackSwipeGuard(!!analysis, t);

  // Listen for inline asking-price submissions from <PriceModal> when the user
  // had no subject_price yet. We DON'T re-run the full AI analysis (that
  // would unmount the modal, fire the loading overlay, and bounce the user
  // back to the verdict screen — terrible UX). Instead we call the cheap
  // subject_price overlay endpoint, which recomputes price_check, the
  // worthify score, and ai_analysis.price_evaluation from the cached
  // anchor-free baseline. Patch the result into existing `analysis` state
  // so the open modal re-renders with the new FAIR/OVERPRICED/SUSPICIOUS
  // verdict in-place.
  useEffect(() => {
    const onAskingPrice = async (e) => {
      const price = Number(e?.detail?.price);
      if (!price || price < 100) return;
      const reg = analysis?.vehicle?.registration || registration;
      if (!reg) return;
      setTargetPrice(String(price));
      try {
        const paidSession = getPaidSession(reg);
        const sidQs = paidSession?.sessionId
          ? `&session_id=${encodeURIComponent(paidSession.sessionId)}`
          : '';
        const r = await queryClient.fetchQuery({
          queryKey: ['asking-price', reg, language, price, sidQs],
          queryFn: async () => {
            const res = await axios.get(
              `${API}/result/${encodeURIComponent(reg)}?language=${language}&subject_price=${price}${sidQs}`
            );
            return res.data;
          },
          staleTime: 1000 * 60 * 5, // Cache for 5 mins
        });
        const overlayed = r?.result;
        if (overlayed && typeof overlayed === 'object') {
          // Merge — keep existing analysis fields the overlay endpoint doesn't return
          // (e.g. detectedIssues that aren't part of the cached result).
          setAnalysis((prev) => ({ ...(prev || {}), ...overlayed }));
          // Celebratory haptic — short-long-short pattern signals "result is in".
          // The verdict-headline spring entrance lands ~120ms after this, so the
          // buzz precedes the visual swap by a beat — premium tactile loop.
          try {
            const verdict = (overlayed?.ai_analysis?.price_evaluation || '').toLowerCase();
            if (navigator.vibrate) {
              navigator.vibrate(verdict === 'fair' ? [12, 50, 30] : [20, 40, 60]);
            }
          } catch { /* noop */ }
          trackEvent('asking_price_inline_submitted', { registration: reg, price });
        }
      } catch (err) {
        console.error('Inline asking-price overlay failed', err);
      }
    };
    window.addEventListener("worthify:set-asking-price", onAskingPrice);
    return () => window.removeEventListener("worthify:set-asking-price", onAskingPrice);
  }, [registration, analysis?.vehicle?.registration, language]);

  // ── Language switch → refetch report content in the new language ──
  // Backend copy (simple explanation, summaries, scripts) is generated per
  // (registration, language). We swap the analysis payload IN PLACE via
  // /api/result so the current screen (dashboard/verdict) stays put; only
  // when no cached analysis exists in the new language do we fall back to
  // the full pipeline.
  useEffect(() => {
    const reg = analysis?.vehicle?.registration;
    const contentLang = analysis?.content_language;
    if (!reg || !contentLang || contentLang === language) return;
    let cancelled = false;
    (async () => {
      try {
        const sid = getPaidSession(reg)?.sessionId;
        const data = await queryClient.fetchQuery({
          queryKey: ['result', reg, language, sid],
          queryFn: async () => {
            const url = `${API}/result/${encodeURIComponent(reg)}?language=${language}`
              + (sid ? `&session_id=${encodeURIComponent(sid)}` : "");
            const res = await axios.get(url, { timeout: 25000 });
            return res.data;
          },
          staleTime: 1000 * 60 * 10, // Cache for 10 mins
        });
        if (!cancelled && data?.result) setAnalysis(data.result);
      } catch {
        // No cached analysis in this language yet — run the full pipeline.
        if (!cancelled) analyzeVehicle({ registration: reg });
      }
    })();
    return () => { cancelled = true; };
  }, [language]);

  // ── Stripe return handling (runs once on mount) ───────────────────
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const purpose = urlParams.get('purpose');
    const paymentParam = urlParams.get('payment');

    // Stripe `cancel_url` brings the user back with ?payment=cancelled.
    // Clear any stale loading flags + show a friendly banner so they
    // understand nothing was charged and they can retry.
    if (paymentParam === 'cancelled') {
      trackEvent('payment_abandoned', { purpose: purpose || 'verdict' });
      setPaymentCancelledBanner(true);
      setPaymentLoading(false);
      setPdfPaymentLoading(false);
      window.history.replaceState({}, document.title, window.location.pathname);
      // Auto-dismiss after 8s so it doesn't linger.
      setTimeout(() => setPaymentCancelledBanner(false), 8000);
      // Restore the saved analysis so the user returns to the verdict screen
      // (not the empty landing) and can immediately retry from the same context.
      const savedAnalysis = localStorage.getItem('pendingAnalysis');
      if (savedAnalysis) {
        try {
          const parsed = JSON.parse(savedAnalysis);
          setAnalysis(parsed);
          // For verdict purpose the user is mid-funnel → show verdict screen.
          // For pdf purpose they already had the dashboard → keep dashboard.
          if (purpose !== 'pdf') setShowVerdict(true);
        } catch { /* ignore */ }
      }
      return;
    }

    if (sessionId) {
      const savedAnalysis = localStorage.getItem('pendingAnalysis');
      if (savedAnalysis) {
        try {
          const parsedAnalysis = JSON.parse(savedAnalysis);
          setAnalysis(parsedAnalysis);

          if (purpose === 'pdf') {
            // PDF payment return — skip verdict, show full report, poll then download
            const savedReg = localStorage.getItem('pendingPdfReg') || parsedAnalysis?.vehicle?.registration || '';
            const savedLang = localStorage.getItem('pendingPdfLang') || 'en';
            const savedPrice = localStorage.getItem('pendingPdfPrice') || '';
            if (savedLang) setLanguage(savedLang);
            if (savedReg) setRegistration(savedReg);
            if (savedPrice) setTargetPrice(savedPrice);
            setShowVerdict(false);
            setPdfPaymentLoading(true);
            // Defensive: ensure no stale post-verdict overlay is shown on this return path
            setExpertConfirm({ visible: false, from: null, to: null });
            setShowLoadingOverlay(false);
            window.history.replaceState({}, document.title, window.location.pathname);
            pollPdfPaymentStatus(sessionId, savedReg, savedLang, savedPrice);
          } else {
            // Verdict payment return — show verdict → on paid → show full report
            const askingFromLs = localStorage.getItem('pendingAskingPrice') || '';
            if (askingFromLs) setTargetPrice(askingFromLs);
            setShowVerdict(true);
            setPaymentLoading(true);
            window.history.replaceState({}, document.title, window.location.pathname);
            pollPaymentStatus(sessionId);
          }
        } catch { /* ignore */ }
      }
    }

    // ── Share-with-mechanic deeplink ?reg=AB12CDE ──────────────────
    // When the recipient opens the shared link, pre-fill the landing form
    // and clean the URL. Doesn't auto-submit — the recipient still has to
    // tap "Check now" so they're in control of the cache + payment flow.
    if (!sessionId && !paymentParam) {
      const regParam = (urlParams.get("reg") || "").trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
      if (regParam && regParam.length >= 2 && regParam.length <= 8) {
        setRegistration(regParam);
        trackEvent("opened_shared_link", { registration: regParam });
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, []);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      analyzeVehicle();
    }
  };

  return (
    <div
      className={`min-h-screen relative ${analysis && !showVerdict ? 'bg-[#07090f]' : (analysis && showVerdict ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950' : '')}`}
    >
      <SeoMeta analysis={analysis} />
      {/* Animated Aurora Background — show on landing AND dashboard for premium aesthetic */}
      {!analysis && (
        <div className="aurora-bg aurora-dark" aria-hidden="true" />
      )}
      {analysis && !showVerdict && (
        <div className="aurora-dashboard" aria-hidden="true">
          <div className="aurora-orb3" aria-hidden="true" />
        </div>
      )}

      <AnalyzingOverlay isVisible={showLoadingOverlay} progress={loadingProgress} providers={loadingProviders} context={loadingContext} t={t} />

      {/* Dedicated full-screen payment verification — covers the Stripe
          return polling so the user never "bounces back" to the previous
          screen with a tiny banner. */}
      <PaymentVerifyingOverlay visible={paymentLoading || pdfPaymentLoading || paymentConfirmedFlash} success={paymentConfirmedFlash} t={t} />

      {/* £2.99 seller-call payment success toast — pops in instantly after
          the Stripe redirect, before the heavier analyze flow has resolved. */}
      <SellerCallSuccessToast language={language} />

      <PaymentCancelledBanner
        visible={paymentCancelledBanner}
        onDismiss={() => setPaymentCancelledBanner(false)}
        t={t}
      />

      <ExpertConfirmationOverlay
        state={expertConfirm}
        t={t}
        onComplete={(finalAnalysis) => {
          // Swap preliminary → final analysis, then close the overlay.
          if (finalAnalysis) setAnalysis(finalAnalysis);
          setExpertConfirm({ visible: false, from: null, to: null });
        }}
      />

      {!analysis && <LandingBackdrop />}

      {/* Header — only shown on landing + paywall screens. Post-paywall mobile
          dashboard renders its own 60px sticky header. */}
      {!useResultHeaderStyle && (
        <AppHeader
          t={t}
          language={language}
          setLanguage={setLanguage}
          onLogoClick={() => {
            // Logo → home. Reset state and push the SPA back to /.
            setAnalysis(null);
            setRegistration("");
            setTargetPrice("");
            setVinNumber("");
            setError("");
            navigate('/');
          }}
        />
      )}

      <main className={`${analysis && !showVerdict ? '' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'} relative z-10`}>
        <Sentry.ErrorBoundary fallback={ErrorFallback} onReset={() => setAnalysis(null)}>
          <Suspense fallback={<div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-slate-700 border-t-emerald-500 rounded-full animate-spin"></div></div>}>
            <AnimatePresence mode="wait">
          {!analysis ? (
            <LandingForm
              key="search-form"
              t={t}
              API={API}
              registration={registration}
              setRegistration={setRegistration}
              targetPrice={targetPrice}
              setTargetPrice={setTargetPrice}
              vinNumber={vinNumber}
              setVinNumber={setVinNumber}
              vinError={vinError}
              setVinError={setVinError}
              vinPreview={vinPreview}
              setVinPreview={setVinPreview}
              vinPreviewLoading={vinPreviewLoading}
              setVinPreviewLoading={setVinPreviewLoading}
              loading={loading}
              error={error}
              setError={setError}
              onSubmit={analyzeVehicle}
              onKeyPress={handleKeyPress}
            />
          ) : showVerdict ? (
            <VerdictScreen
              analysis={analysis}
              t={t}
              language={language}
              paymentLoading={paymentLoading}
              paymentError={paymentError}
              getVerdictBg={getVerdictBg}
              getVerdictKey={getVerdictKey}
              getPaywallTeaserKey={getPaywallTeaserKey}
              onUnlock={initiatePayment}
              onBack={() => { _analysisState.reset(); }}
            />
          ) : (
            <motion.div
              key="analysis-results"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            >
              {pdfPaymentLoading && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="max-w-[430px] mx-auto mt-3 mb-2 rounded-xl px-4 py-2.5 flex items-center gap-2"
                  style={{
                    background: "rgba(16, 185, 129, 0.15)",
                    border: "1px solid rgba(16, 185, 129, 0.3)",
                    backdropFilter: "blur(12px)",
                    WebkitBackdropFilter: "blur(12px)",
                  }}
                  data-testid="pdf-payment-verifying"
                >
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="w-4 h-4 border-2 border-t-transparent rounded-full flex-shrink-0"
                    style={{ borderColor: "#34D399", borderTopColor: "transparent" }}
                  />
                  <p style={{ color: "#34D399" }} className="text-xs font-medium">{t('paymentVerifyingBanner')}</p>
                </motion.div>
              )}
              <MobileDashboard
                analysis={analysis}
                t={t}
                language={language}
                setLanguage={setLanguage}
                languages={LANGUAGES}
                onNewSearch={() => { setAnalysis(null); setRegistration(""); setError(""); }}
                onNewSearchWithPayment={startNewSearchWithPayment}
                onReanalyzeWithVin={reanalyzeWithVin}
                onDownloadPdf={initiatePdfPayment}
                pdfUnlocked={pdfUnlocked}
                downloading={pdfPaymentLoading || downloadingPdf}
                detectedIssues={detectedIssues}
                formatDate={(d) => formatDate(d, uiDateLocale(language))}
                getVerdictKey={getVerdictKey}
                getVerdictShortExplanationKey={getVerdictShortExplanationKey}
                onLogoClick={() => {
                  setAnalysis(null);
                  setRegistration("");
                  setTargetPrice("");
                  setVinNumber("");
                  setError("");
                  navigate('/');
                }}
              />
            </motion.div>
          )}
            </AnimatePresence>
          </Suspense>
        </Sentry.ErrorBoundary>
      </main>

      {/* Legal footer — visible only on the landing page */}
      {!analysis && <LegalFooter t={t} />}

      {/* Subtle "get the app" prompt — landing only */}
      {!analysis && <AppDownloadBanner t={t} />}

      <CookieBanner t={t} language={language} />

      {/* Pre-checkout consent modal — UK Consumer Contracts Regs 2013 */}
      <PreCheckoutConsentModal
        open={consentModal.open}
        onClose={() => setConsentModal((c) => ({ ...c, open: false }))}
        onConfirm={performStripeRedirect}
        amount={consentModal.amount}
        purpose={consentModal.purpose}
        registration={consentModal.registration}
        t={t}
      />
    </div>
  );
};

// Small wrapper so static legal pages get current language + `t` without re-rendering Home.
const LegalPageWrapper = ({ Component }) => {
  const language = (() => {
    try { return localStorage.getItem('worthify_lang') || 'en'; } catch { return 'en'; }
  })();
  const t = (key) => {
    const lang = translations[language] || translations.en;
    return lang[key] || translations.en[key] || key;
  };
  return <Component t={t} language={language} />;
};

function App() {
  React.useEffect(() => {
    cleanupStalePaidSessions();
  }, []);
  return (
    <div className="App">
      <Toaster position="top-center" richColors closeButton theme="dark" />
      <BrowserRouter>
        <Routes>
          {/* Marketing layout — keeps <Home/> mounted underneath while a
              modal route swaps in via <Outlet/>. Hovering between nav
              links (About → Pricing → FAQ → Contact …) updates the URL
              via `replace:true`, which swaps the modal without ever
              remounting the landing page below. Closing returns to /. */}
          <Route element={<><Home /><Suspense fallback={null}><Outlet /></Suspense></>}>
            <Route path="/" element={null} />
            <Route path="/report/:reg" element={null} />
            <Route path="/how-it-works"      element={<MarketingModalHost />} />
            <Route path="/pricing"           element={<MarketingModalHost />} />

            <Route path="/why-worthify"      element={<MarketingModalHost />} />
            <Route path="/download"          element={<MarketingModalHost />} />
            <Route path="/ai-call-assistant" element={<MarketingModalHost />} />
            <Route path="/ai-call"           element={<MarketingModalHost />} />
            <Route path="/about"             element={<MarketingModalHost />} />
            <Route path="/for-investors"     element={<MarketingModalHost />} />
            <Route path="/investors"         element={<MarketingModalHost />} />
            <Route path="/faq"               element={<MarketingModalHost />} />
            <Route path="/contact"           element={<MarketingModalHost />} />
          </Route>

          {/* Legal pages — standalone, no homepage hero. */}
          <Route path="/terms" element={<LegalPageWrapper Component={TermsPage} />} />
          <Route path="/privacy" element={<LegalPageWrapper Component={PrivacyPage} />} />
          <Route path="/cookies" element={<LegalPageWrapper Component={CookiePolicyPage} />} />
          <Route path="/accessibility" element={<LegalPageWrapper Component={AccessibilityPage} />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
